import React, { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import { useSocket } from '../context/SocketContext';
import { formatPhone } from '../utils/formatPhone';

export default function Chats() {
  const { socket } = useSocket();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [lead, setLead] = useState(null);
  const [reply, setReply] = useState('');
  const [botPaused, setBotPaused] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.phone === data.phone);
        if (idx === -1) {
          loadConversations();
          return prev;
        }
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          lastMessage: { role: data.role, content: data.content },
          lastMessageAt: data.timestamp,
        };
        const [item] = updated.splice(idx, 1);
        return [item, ...updated];
      });
      if (selected && data.phone === selected) {
        setMessages((prev) => [...prev, { role: data.role, content: data.content, timestamp: data.timestamp }]);
      }
    };
    socket.on('new_message', handler);
    return () => socket.off('new_message', handler);
  }, [socket, selected]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadConversations() {
    try {
      const { data } = await api.get('/api/chats?limit=50');
      setConversations(data.data || []);
    } catch {}
  }

  async function openChat(phone) {
    setSelected(phone);
    try {
      const { data } = await api.get(`/api/chats/${encodeURIComponent(phone)}`);
      setMessages(data.conversation?.messages || []);
      setLead(data.lead);
      setBotPaused(data.lead?.botPaused || false);
    } catch {}
  }

  async function sendReply() {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/api/chats/${encodeURIComponent(selected)}/reply`, { message: reply });
      setMessages((prev) => [...prev, { role: 'assistant', content: reply, timestamp: new Date().toISOString() }]);
      setReply('');
    } catch (err) {
      alert('Failed to send: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  }

  async function toggleBot() {
    const endpoint = botPaused ? 'resume' : 'pause';
    await api.post(`/api/chats/${encodeURIComponent(selected)}/${endpoint}`);
    setBotPaused(!botPaused);
  }

  async function deleteChat(phone) {
    if (!window.confirm('Delete this entire conversation? This cannot be undone.')) return;
    await api.delete(`/api/chats/${encodeURIComponent(phone)}`);
    setConversations(prev => prev.filter(c => c.phone !== phone));
    if (selected === phone) { setSelected(null); setMessages([]); }
  }

  const filtered = conversations.filter((c) => {
    if (!searchQ) return true;
    const phone = formatPhone(c.phone);
    const name = c.lead?.name || '';
    return phone.includes(searchQ) || name.toLowerCase().includes(searchQ.toLowerCase());
  });

  return (
    <div style={styles.container}>
      <div style={styles.leftPanel}>
        <div style={styles.leftHeader}>
          <h2 style={styles.panelTitle}>Conversations</h2>
          <input
            style={styles.search}
            placeholder="Search by name / phone..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
        </div>
        <div style={styles.chatList}>
          {filtered.length === 0 && <p style={styles.empty}>No conversations yet</p>}
          {filtered.map((c) => {
            const phone = formatPhone(c.phone);
            const last = c.lastMessage;
            const isActive = selected === c.phone;
            return (
              <div
                key={c._id || c.phone}
                style={{ ...styles.chatItem, ...(isActive ? styles.chatItemActive : {}) }}
                onClick={() => openChat(c.phone)}
              >
                <div style={styles.avatar}>{c.lead?.name?.[0]?.toUpperCase() || phone[0]}</div>
                <div style={styles.chatItemInfo}>
                  <div style={styles.chatItemName}>{c.lead?.name ? `${c.lead.name} (${phone})` : phone}</div>
                  <div style={styles.chatItemLast}>
                    {last ? (last.role === 'assistant' ? '🤖 ' : '👤 ') + last.content?.slice(0, 45) + '...' : 'No messages'}
                  </div>
                </div>
                {c.lead?.wantsHuman && <span style={styles.alertDot} title="Wants human" />}
                <button
                  onClick={(e) => { e.stopPropagation(); deleteChat(c.phone); }}
                  title="Delete conversation"
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, padding: '2px 4px', opacity: 0.6 }}
                >🗑</button>
              </div>
            );
          })}
        </div>
      </div>

      <div style={styles.rightPanel}>
        {!selected ? (
          <div style={styles.noChat}>
            <span style={{ fontSize: 48 }}>💬</span>
            <p>Select a conversation to view</p>
          </div>
        ) : (
          <>
            <div style={styles.chatHeader}>
              <div style={styles.chatHeaderLeft}>
                <div style={styles.avatarLg}>{lead?.name?.[0]?.toUpperCase() || '?'}</div>
                <div>
                  <div style={styles.chatHeaderName}>{lead?.name ? `${lead.name} (${formatPhone(selected)})` : formatPhone(selected)}</div>
                  <div style={styles.chatHeaderSub}>
                    {lead?.interest && <span style={styles.tag}>🎯 {lead.interest}</span>}
                    {lead?.background && <span style={styles.tag}>💼 {lead.background}</span>}
                    {lead?.wantsHuman && <span style={{ ...styles.tag, background: '#ec4899' }}>🙋 Wants Human</span>}
                  </div>
                </div>
              </div>
              <button
                style={{ ...styles.toggleBtn, background: botPaused ? '#22c55e22' : '#f9731622', color: botPaused ? '#22c55e' : '#f97316', borderColor: botPaused ? '#22c55e' : '#f97316' }}
                onClick={toggleBot}
              >
                {botPaused ? '▶ Resume Bot' : '⏸ Pause Bot'}
              </button>
            </div>

            <div style={styles.messages}>
              {messages.map((m, i) => (
                <div key={i} style={{ ...styles.msgWrapper, justifyContent: m.role === 'assistant' ? 'flex-end' : 'flex-start', flexDirection: 'column', alignItems: m.role === 'assistant' ? 'flex-end' : 'flex-start' }}>
                  <span style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, marginLeft: 6, marginRight: 6 }}>
                    {m.role === 'assistant' ? '🤖 AI Bot' : (lead?.name || formatPhone(selected))}
                  </span>
                  <div style={{ ...styles.bubble, ...(m.role === 'assistant' ? styles.bubbleBot : styles.bubbleUser) }}>
                    <p style={styles.bubbleText}>{m.content}</p>
                    <span style={styles.bubbleTime}>{formatTime(m.timestamp)}</span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div style={styles.replyBar}>
              {botPaused && <div style={styles.pausedBanner}>🤖 Bot paused — you are in control</div>}
              <div style={styles.replyRow}>
                <textarea
                  style={styles.replyInput}
                  rows={2}
                  placeholder={botPaused ? 'Type your reply...' : 'Pause bot to manually reply...'}
                  value={reply}
                  disabled={!botPaused}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                />
                <button style={styles.sendBtn} onClick={sendReply} disabled={!botPaused || sending || !reply.trim()}>
                  {sending ? '...' : '➤'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const styles = {
  container: { display: 'flex', flex: 1, height: '100vh', overflow: 'hidden' },
  leftPanel: { width: 300, borderRight: '1px solid #334155', display: 'flex', flexDirection: 'column', background: '#1e293b' },
  leftHeader: { padding: '16px 14px', borderBottom: '1px solid #334155' },
  panelTitle: { fontSize: 16, fontWeight: 700, marginBottom: 10, color: '#f1f5f9' },
  search: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: 13, outline: 'none' },
  chatList: { flex: 1, overflowY: 'auto' },
  empty: { padding: 16, color: '#64748b', fontSize: 13 },
  chatItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid #0f172a', position: 'relative' },
  chatItemActive: { background: '#334155' },
  avatar: { width: 38, height: 38, borderRadius: '50%', background: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0, color: '#f1f5f9' },
  chatItemInfo: { flex: 1, overflow: 'hidden' },
  chatItemName: { fontWeight: 600, fontSize: 14, color: '#f1f5f9', marginBottom: 2 },
  chatItemLast: { fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  alertDot: { width: 8, height: 8, borderRadius: '50%', background: '#ec4899', flexShrink: 0 },
  rightPanel: { flex: 1, display: 'flex', flexDirection: 'column', background: '#0f172a' },
  noChat: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#475569', gap: 12 },
  chatHeader: { padding: '14px 20px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b' },
  chatHeaderLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  avatarLg: { width: 44, height: 44, borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: '#fff' },
  chatHeaderName: { fontWeight: 700, fontSize: 15, color: '#f1f5f9', marginBottom: 4 },
  chatHeaderSub: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  tag: { fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#334155', color: '#94a3b8' },
  toggleBtn: { padding: '7px 14px', borderRadius: 8, border: '1px solid', fontSize: 13, cursor: 'pointer', fontWeight: 600, background: 'transparent' },
  messages: { flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 8 },
  msgWrapper: { display: 'flex' },
  bubble: { maxWidth: '68%', padding: '10px 14px', borderRadius: 12 },
  bubbleBot: { background: '#6366f1', borderBottomRightRadius: 3 },
  bubbleUser: { background: '#1e293b', border: '1px solid #334155', borderBottomLeftRadius: 3 },
  bubbleText: { fontSize: 14, color: '#f1f5f9', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  bubbleTime: { fontSize: 10, color: 'rgba(255,255,255,0.5)', display: 'block', marginTop: 4, textAlign: 'right' },
  replyBar: { borderTop: '1px solid #334155', padding: '12px 16px', background: '#1e293b' },
  pausedBanner: { fontSize: 12, color: '#22c55e', marginBottom: 8, fontWeight: 500 },
  replyRow: { display: 'flex', gap: 8, alignItems: 'flex-end' },
  replyInput: { flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit' },
  sendBtn: { width: 44, height: 44, borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontSize: 18, cursor: 'pointer', flexShrink: 0 },
};
